import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './LoginPage.css';

const LoginPage = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setFocus
  } = useForm();

  const { login, isLoginLoading, error, clearError, isAuthenticated } = useAuth();
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Focus on username field on mount
  useEffect(() => {
    setFocus('username');
  }, [setFocus]);

  // Clear error when component unmounts or user starts typing
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const onSubmit = async (data) => {
    clearError();
    const result = await login({
      username: data.username,
      password: data.password
    }, rememberMe);

    if (result.success) {
      // Get redirect path or default to dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  };

  if (isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="login-container">
      {/* Decorative elements for background */}
      <div className="bg-circle bg-circle-1"></div>
      <div className="bg-circle bg-circle-2"></div>
      <div className="bg-circle bg-circle-3"></div>
      <div className="bg-circle bg-circle-4"></div>
      
      <div className="login-page-wrapper">
        {/* Left side - Login Card */}
        <div className="login-card">
          <div className="login-header">
            <h1>Venturi IMS</h1>
            <p>Inventory Management System</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="login-form" noValidate>
            <h2>Sign In</h2>

            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="username">
                Username <span className="required">*</span>
              </label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                className={errors.username ? 'error' : ''}
                {...register('username', {
                  required: 'Username is required',
                  minLength: {
                    value: 3,
                    message: 'Username must be at least 3 characters'
                  }
                })}
                aria-describedby={errors.username ? 'username-error' : undefined}
              />
              {errors.username && (
                <span id="username-error" className="field-error" role="alert">
                  {errors.username.message}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password">
                Password <span className="required">*</span>
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                className={errors.password ? 'error' : ''}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  }
                })}
                aria-describedby={errors.password ? 'password-error' : undefined}
              />
              {errors.password && (
                <span id="password-error" className="field-error" role="alert">
                  {errors.password.message}
                </span>
              )}
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="checkbox-text">Remember me</span>
              </label>

              <Link to="/forgot-password" className="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={isLoginLoading}
              aria-describedby={isLoginLoading ? 'loading-text' : undefined}
            >
              {isLoginLoading ? (
                <>
                  <span className="spinner" aria-hidden="true"></span>
                  <span id="loading-text">Signing in...</span>
                </>
              ) : (
                'Sign In'
              )}
            </button>

          </form>

          <div className="login-footer">
            <p>&copy; 2024 Venturi IMS. All rights reserved.</p>
          </div>
        </div>

        {/* Right side - Welcome Section */}
        <div className="welcome-section">
          {/* Animated background elements */}
          <div className="animated-bg">
            <div className="orbit orbit-1"></div>
            <div className="orbit orbit-2"></div>
            <div className="orbit orbit-3"></div>
            <div className="blob blob-1"></div>
            <div className="blob blob-2"></div>
            <div className="blob blob-3"></div>
          </div>
          
          <div className="welcome-content">
            <h1>Welcome to Venturi IMS</h1>
            <h2>Inventory Management System</h2>
            <p>Streamline your inventory operations with our comprehensive management solution.</p>
            <div className="welcome-features">
              <div className="feature-item">
                <div className="feature-icon">📊</div>
                <div className="feature-text">Real-time Analytics</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">🔄</div>
                <div className="feature-text">Seamless Integration</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">📱</div>
                <div className="feature-text">Mobile Accessibility</div>
              </div>
            </div>
          </div>
          <div className="wave-animation"></div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;